import { lazy, Suspense, type ComponentType } from 'react';
import { Route, Switch } from 'wouter';

import { OwnerRoute, ProtectedRoute } from '@/components/auth/protected-route';
import { ErrorBoundary } from '@/components/feedback/error-boundary';
import { FullPageLoader } from '@/components/feedback/full-page-loader';
import { AppLayout } from '@/layouts/app-layout';
import { FinancialAccessGate } from '@/components/auth/financial-access-gate';

const pages: Record<string, ComponentType> = {
  '/app/primeiros-passos': lazy(() => import('@/pages/onboarding-page')),
  '/app': lazy(() => import('@/pages/dashboard-page')),
  '/estoque': lazy(() => import('@/pages/inventory-page')),
  '/estoque/pre-preparos': lazy(() => import('@/pages/preparations-page')),
  '/financeiro/canais': lazy(() => import('@/pages/sales-channels-page')),
  '/financeiro/importacao-itens': lazy(() => import('@/pages/item-sales-import-page')),
  '/operacoes-em-lote': lazy(() => import('@/pages/bulk-operations-page')),
  '/compras': lazy(() => import('@/pages/purchases-page')),
  '/fornecedores': lazy(() => import('@/pages/suppliers-page')),
  '/motoboys': lazy(() => import('@/pages/delivery-team-page')),
  '/historico-de-compras': lazy(() => import('@/pages/purchase-history-page')),
  '/lista-de-compras': lazy(() => import('@/pages/shopping-list-page')),
  '/produtos': lazy(() => import('@/pages/products-page')),
  '/ficha-tecnica': lazy(() => import('@/pages/recipe-page')),
  '/financeiro': lazy(() => import('@/pages/finance-page')),
  '/financeiro/importacao-vendas': lazy(() => import('@/pages/sales-import-page')),
  '/financeiro/formas-pagamento': lazy(() => import('@/pages/payment-methods-page')),
  '/dre': lazy(() => import('@/pages/dre-page')),
  '/cmv': lazy(() => import('@/pages/cmv-page')),
  '/analises': lazy(() => import('@/pages/analytics-page')),
  '/usuarios': lazy(() => import('@/pages/users-page')),
  '/seguranca': lazy(() => import('@/pages/security-page')),
  '/sanitario': lazy(() => import('@/pages/sanitary-dashboard-page')),
  '/sanitario/fiscalizacoes': lazy(() => import('@/pages/sanitary-inspections-page')),
  '/sanitario/agentes': lazy(() => import('@/pages/sanitary-agents-page')),
  '/sanitario/documentos': lazy(() => import('@/pages/sanitary-documents-page')),
  '/sanitario/checklists': lazy(() => import('@/pages/sanitary-checklists-page')),
  '/sanitario/relatorios': lazy(() => import('@/pages/sanitary-reports-page')),
};

const LoginPage = lazy(() => import('@/pages/login-page'));
const PublicTrialPage = lazy(() => import('@/pages/public-trial-page'));
const TeamInvitePage = lazy(() => import('@/pages/team-invite-page'));
const ForgotPasswordPage = lazy(() => import('@/pages/forgot-password-page'));
const ResetPasswordPage = lazy(() => import('@/pages/reset-password-page'));
const EmailVerificationPage = lazy(() => import('@/pages/email-verification-page'));
const LandingPage = lazy(() => import('@/pages/landing-page'));
const BlogPage = lazy(() => import('@/pages/blog-page'));
const BlogPostPage = lazy(() => import('@/pages/blog-post-page'));
const PrivacyPage = lazy(() => import('@/pages/privacy-page'));
const TermsPage = lazy(() => import('@/pages/terms-page'));
const MasterLoginPage = lazy(() => import('@/pages/master-login-page'));
const MasterPage = lazy(() => import('@/pages/master-page'));
const ResellerLoginPage = lazy(() => import('@/pages/reseller-login-page'));
const ResellerPage = lazy(() => import('@/pages/reseller-page'));
const AffiliateLoginPage = lazy(() => import('@/pages/affiliate-login-page'));
const AffiliateDashboardPage = lazy(() => import('@/pages/affiliate-dashboard-page'));
const AffiliateClientsPage = lazy(() => import('@/pages/affiliate-clients-page'));
const AffiliateCommissionsPage = lazy(() => import('@/pages/affiliate-commissions-page'));
const AffiliatePayoutsPage = lazy(() => import('@/pages/affiliate-payouts-page'));
const AffiliateMaterialsPage = lazy(() => import('@/pages/affiliate-materials-page'));
const AffiliateProfilePage = lazy(() => import('@/pages/affiliate-profile-page'));
const AffiliateSupportPage = lazy(() => import('@/pages/affiliate-support-page'));
const NotFoundPage = lazy(() => import('@/pages/not-found-page'));
const ImpersonationPage = lazy(() => import('@/pages/impersonation-page'));
const CustomerBillingPage = lazy(() => import('@/pages/customer-billing-page'));
const CustomerFinancialPortalPage = lazy(() => import('@/pages/customer-financial-portal-page'));
const SupportPage = lazy(() => import('@/pages/support-page'));
const ContractSignPage = lazy(() => import('@/pages/contract-sign-page'));
const MasterInvitePage = lazy(() => import('@/pages/master-invite-page'));

function ProtectedPages() {
  return (
    <ProtectedRoute>
      <FinancialAccessGate>
        <AppLayout>
          <Switch>
            {Object.entries(pages).map(([path, Page]) => (
              <Route key={path} path={path}>
                <Page />
              </Route>
            ))}
            <Route>
              <NotFoundPage />
            </Route>
          </Switch>
        </AppLayout>
      </FinancialAccessGate>
    </ProtectedRoute>
  );
}

export function AppRouter() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<FullPageLoader />}>
        <Switch>
          <Route path="/comecar">
            <PublicTrialPage />
          </Route>
          <Route path="/convite-equipe">
            <TeamInvitePage />
          </Route>
          <Route path="/contratos/assinar/:token">
            <ContractSignPage />
          </Route>
          <Route path="/blog/:slug">
            <BlogPostPage />
          </Route>
          <Route path="/blog">
            <BlogPage />
          </Route>
          <Route path="/master/login">
            <MasterLoginPage />
          </Route>
          <Route path="/master/convite">
            <MasterInvitePage />
          </Route>
          <Route path="/master/revendedores">
            <MasterPage view="resellers" />
          </Route>
          <Route path="/master/afiliados">
            <MasterPage view="affiliates" />
          </Route>
          <Route path="/master/clientes">
            <MasterPage view="clients" />
          </Route>
          <Route path="/master/licencas">
            <MasterPage view="licenses" />
          </Route>
          <Route path="/master/planos">
            <MasterPage view="plans" />
          </Route>
          <Route path="/master/financeiro">
            <MasterPage view="finance" />
          </Route>
          <Route path="/master/cobrancas">
            <MasterPage view="billing" />
          </Route>
          <Route path="/master/contratos">
            <MasterPage view="contracts" />
          </Route>
          <Route path="/master/solicitacoes-upgrade">
            <MasterPage view="upgradeRequests" />
          </Route>
          <Route path="/master/integracoes">
            <MasterPage view="integrations" />
          </Route>
          <Route path="/master/cotacoes">
            <MasterPage view="quotes" />
          </Route>
          <Route path="/master/blog">
            <MasterPage view="blog" />
          </Route>
          <Route path="/master/relatorios">
            <MasterPage view="reports" />
          </Route>
          <Route path="/master/suporte">
            <MasterPage view="support" />
          </Route>
          <Route path="/master/logs">
            <MasterPage view="logs" />
          </Route>
          <Route path="/master/configuracoes">
            <MasterPage view="settings" />
          </Route>
          <Route path="/master/equipe">
            <MasterPage view="team" />
          </Route>
          <Route path="/master/atualizacoes">
            <MasterPage view="updates" />
          </Route>
          <Route path="/master">
            <MasterPage view="dashboard" />
          </Route>
          <Route path="/administracao/afiliados">
            <MasterPage view="affiliates" />
          </Route>
          <Route path="/administracao">
            <MasterPage view="dashboard" />
          </Route>

          <Route path="/revendedor/login">
            <ResellerLoginPage />
          </Route>
          <Route path="/revendedor/clientes">
            <ResellerPage view="clients" />
          </Route>
          <Route path="/revendedor/licencas">
            <ResellerPage view="licenses" />
          </Route>
          <Route path="/revendedor/mensalidades">
            <ResellerPage view="invoices" />
          </Route>
          <Route path="/revendedor/financeiro">
            <ResellerPage view="finance" />
          </Route>
          <Route path="/revendedor/relatorios">
            <ResellerPage view="reports" />
          </Route>
          <Route path="/revendedor/suporte">
            <ResellerPage view="support" />
          </Route>
          <Route path="/revendedor/perfil">
            <ResellerPage view="profile" />
          </Route>
          <Route path="/revendedor">
            <ResellerPage view="dashboard" />
          </Route>

          <Route path="/afiliado/login">
            <AffiliateLoginPage />
          </Route>
          <Route path="/afiliado/clientes">
            <AffiliateClientsPage />
          </Route>
          <Route path="/afiliado/comissoes">
            <AffiliateCommissionsPage />
          </Route>
          <Route path="/afiliado/pagamentos">
            <AffiliatePayoutsPage />
          </Route>
          <Route path="/afiliado/materiais">
            <AffiliateMaterialsPage />
          </Route>
          <Route path="/afiliado/perfil">
            <AffiliateProfilePage />
          </Route>
          <Route path="/afiliado/suporte">
            <AffiliateSupportPage />
          </Route>
          <Route path="/afiliado">
            <AffiliateDashboardPage />
          </Route>
          <Route path="/afiliados/login">
            <AffiliateLoginPage />
          </Route>
          <Route path="/afiliados/clientes">
            <AffiliateClientsPage />
          </Route>
          <Route path="/afiliados/comissoes">
            <AffiliateCommissionsPage />
          </Route>
          <Route path="/afiliados/pagamentos">
            <AffiliatePayoutsPage />
          </Route>
          <Route path="/afiliados/materiais">
            <AffiliateMaterialsPage />
          </Route>
          <Route path="/afiliados/perfil">
            <AffiliateProfilePage />
          </Route>
          <Route path="/afiliados/suporte">
            <AffiliateSupportPage />
          </Route>
          <Route path="/afiliados">
            <AffiliateDashboardPage />
          </Route>
          <Route path="/acesso-assistido">
            <ImpersonationPage />
          </Route>
          <Route path="/portal-financeiro">
            <OwnerRoute>
              <CustomerFinancialPortalPage />
            </OwnerRoute>
          </Route>
          <Route path="/financeiro/minha-assinatura">
            <OwnerRoute>
              <AppLayout>
                <CustomerBillingPage />
              </AppLayout>
            </OwnerRoute>
          </Route>
          <Route path="/suporte">
            <ProtectedRoute>
              <AppLayout>
                <SupportPage />
              </AppLayout>
            </ProtectedRoute>
          </Route>
          <Route path="/cotacao">
            <LandingPage />
          </Route>
          <Route path="/">
            <LandingPage />
          </Route>
          <Route path="/login">
            <LoginPage />
          </Route>
          <Route path="/esqueci-minha-senha">
            <ForgotPasswordPage />
          </Route>
          <Route path="/redefinir-senha">
            <ResetPasswordPage />
          </Route>
          <Route path="/verificar-email">
            <EmailVerificationPage />
          </Route>
          <Route path="/politica-de-privacidade">
            <PrivacyPage />
          </Route>
          <Route path="/termos-de-uso">
            <TermsPage />
          </Route>
          <Route>
            <ProtectedPages />
          </Route>
        </Switch>
      </Suspense>
    </ErrorBoundary>
  );
}
